import { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorIcon from '@mui/icons-material/Error';
import type { ParagraphDto } from '../../api/queries/books';
import { useAIAction } from '../../hooks/useAIAction';
import { StylePresetSelector } from './StylePresetSelector';

interface BulkGenerationDialogProps {
  open: boolean;
  onClose: () => void;
  bookId: string;
  chapterId?: string;
  paragraphs: ParagraphDto[];
  mode: 'chapter' | 'book';
}

type ItemStatus = 'queued' | 'in-progress' | 'complete' | 'failed';

interface ResultItem {
  id: string;
  status: ItemStatus;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim();
}

function buildPrompt(
  paragraph: ParagraphDto,
  stylePreset: string,
  includeEntities: boolean,
): string {
  const parts: string[] = [];
  const text = stripHtml(paragraph.contentHtml);
  if (text) {
    parts.push(text);
  }
  if (paragraph.sceneDescription) {
    parts.push(`Scene: ${paragraph.sceneDescription}`);
  }
  if (paragraph.mood) {
    parts.push(`Mood: ${paragraph.mood}`);
  }
  if (includeEntities && paragraph.speakingEntityIds.length > 0) {
    parts.push(`Speaking entities: ${paragraph.speakingEntityIds.join(', ')}`);
  }
  if (stylePreset) {
    parts.push(`Style: ${stylePreset}`);
  }
  return parts.join('\n\n');
}

const CREDITS_PER_IMAGE = 5;

/**
 * Dialog for bulk-generating images for an entire chapter or book.
 * Iterates through paragraphs sequentially, calling the image-generation
 * AI action for each one with a short delay between calls.
 */
export function BulkGenerationDialog({
  open,
  onClose,
  bookId: _bookId,
  chapterId: _chapterId,
  paragraphs,
  mode,
}: BulkGenerationDialogProps) {
  const [stylePreset, setStylePreset] = useState('digital-painting');
  const [skipExisting, setSkipExisting] = useState(true);
  const [includeEntities, setIncludeEntities] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<ResultItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const cancelledRef = useRef(false);

  const generateImage = useAIAction('image-generation');

  // Determine which paragraphs need generation
  const filteredParagraphs = skipExisting
    ? paragraphs.filter(
        (p) => !results.some((r) => r.id === p.id && r.status === 'complete'),
      )
    : paragraphs;

  // For cost/summary display, we estimate how many will be processed
  // (paragraphs that haven't been completed in the current run).
  const alreadyComplete = results.filter((r) => r.status === 'complete').length;
  const willGenerate = skipExisting
    ? paragraphs.length - alreadyComplete
    : paragraphs.length;
  const estimatedCredits = willGenerate * CREDITS_PER_IMAGE;

  const title =
    mode === 'chapter'
      ? 'Generate images for chapter'
      : 'Generate images for entire book';

  const handleGenerate = useCallback(async () => {
    cancelledRef.current = false;
    setIsRunning(true);
    setResults([]);
    setCurrentIndex(0);

    const toProcess = skipExisting ? [...filteredParagraphs] : [...paragraphs];

    let idx = 0;
    for (const p of toProcess) {
      if (cancelledRef.current) break;

      setCurrentIndex(idx);
      setResults((prev) => [...prev, { id: p.id, status: 'in-progress' }]);

      try {
        await generateImage.mutateAsync({
          prompt: buildPrompt(p, stylePreset, includeEntities),
          aspect_ratio: '16:9',
          style: stylePreset,
          paragraph_id: p.id,
        });
        setResults((prev) =>
          prev.map((r) => (r.id === p.id ? { ...r, status: 'complete' } : r)),
        );
      } catch {
        setResults((prev) =>
          prev.map((r) => (r.id === p.id ? { ...r, status: 'failed' } : r)),
        );
      }

      idx++;

      // Short delay between calls to avoid overwhelming the queue
      if (!cancelledRef.current && idx < toProcess.length) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }

    setIsRunning(false);
  }, [
    filteredParagraphs,
    paragraphs,
    skipExisting,
    stylePreset,
    includeEntities,
    generateImage,
  ]);

  const handleCancel = () => {
    cancelledRef.current = true;
  };

  const handleClose = () => {
    if (isRunning) {
      cancelledRef.current = true;
    }
    setIsRunning(false);
    setResults([]);
    setCurrentIndex(0);
    onClose();
  };

  const completedCount = results.filter((r) => r.status === 'complete').length;
  const failedCount = results.filter((r) => r.status === 'failed').length;
  const progress =
    results.length > 0 ? (results.length / willGenerate) * 100 : 0;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/* Options */}
          {!isRunning && results.length === 0 && (
            <>
              <StylePresetSelector
                value={stylePreset}
                onChange={setStylePreset}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipExisting}
                    onChange={(e) => setSkipExisting(e.target.checked)}
                  />
                }
                label="Skip paragraphs with existing images"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeEntities}
                    onChange={(e) => setIncludeEntities(e.target.checked)}
                  />
                }
                label="Include entity descriptors in prompts"
              />

              {/* Summary */}
              <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  Will generate images for <strong>{willGenerate}</strong> of{' '}
                  <strong>{paragraphs.length}</strong> paragraphs
                  {skipExisting &&
                    paragraphs.length - willGenerate > 0 &&
                    ` (${paragraphs.length - willGenerate} already have images)`}
                </Typography>
              </Alert>

              {/* Cost estimate */}
              <Typography variant="caption" color="text.secondary">
                Estimated cost: {estimatedCredits} credits ({CREDITS_PER_IMAGE}{' '}
                credits per image)
              </Typography>
            </>
          )}

          {/* Progress section */}
          {(isRunning || results.length > 0) && (
            <Stack spacing={1.5}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="body2">
                {isRunning
                  ? `Generating ${currentIndex + 1} of ${willGenerate}...`
                  : `Complete: ${completedCount} succeeded, ${failedCount} failed`}
              </Typography>

              {/* Paragraph status list */}
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List dense disablePadding>
                  {(skipExisting ? filteredParagraphs : paragraphs).map(
                    (p, idx) => {
                      const result = results.find((r) => r.id === p.id);
                      const status: ItemStatus = result
                        ? result.status
                        : 'queued';
                      return (
                        <ListItem key={p.id} disableGutters sx={{ py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <StatusIcon status={status} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="caption" noWrap>
                                Paragraph {idx + 1}:{' '}
                                {stripHtml(p.contentHtml).slice(0, 60)}
                                {stripHtml(p.contentHtml).length > 60
                                  ? '...'
                                  : ''}
                              </Typography>
                            }
                          />
                        </ListItem>
                      );
                    },
                  )}
                </List>
              </Box>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {isRunning ? (
          <Button onClick={handleCancel} color="warning">
            Cancel
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>
              {results.length > 0 ? 'Close' : 'Cancel'}
            </Button>
            {results.length === 0 && (
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={willGenerate === 0}
              >
                Generate
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

function StatusIcon({ status }: { status: ItemStatus }) {
  switch (status) {
    case 'complete':
      return <CheckCircleIcon fontSize="small" color="success" />;
    case 'in-progress':
      return <HourglassEmptyIcon fontSize="small" color="primary" />;
    case 'failed':
      return <ErrorIcon fontSize="small" color="error" />;
    case 'queued':
    default:
      return <RadioButtonUncheckedIcon fontSize="small" color="disabled" />;
  }
}
